import { useState } from 'react';
import { Outlet } from 'react-router';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Sidebar from './Sidebar';
import UserMenu from './UserMenu';
import NoAccess from './NoAccess';
import { useAuth } from '../lib/AuthContext';

const DRAWER_WIDTH = 260;

/** Layout aus MyHours übernommen: Kopfleiste im Vereinsdesign, ein-/ausklappbares Menü. */
export default function AppShell() {
  // Default open on a desktop-width load, closed on a narrow one -- read once at mount, so a
  // deliberate toggle is never fought by a later render (MyHours).
  const [drawerOpen, setDrawerOpen] = useState(
    () => window.matchMedia('(min-width: 600px)').matches,
  );
  // The temporary Drawer's Modal locks body scroll off its `open` prop even while CSS-hidden at
  // sm+; gate it on the live viewport so a desktop page never becomes unscrollable (MyHours).
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const { hasAccess } = useAuth();

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1, displayPrint: 'none' }}>
        <Toolbar>
          {hasAccess && (
            <IconButton
              color="inherit"
              edge="start"
              aria-label="Menü ein-/ausblenden"
              onClick={() => setDrawerOpen((o) => !o)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box
            component="img"
            src="/club-logo.png"
            alt=""
            sx={{
              height: 40,
              mr: 1.5,
              display: { xs: 'none', sm: 'block' },
              // Weiße Kontur, damit das schwarz umrandete Wappen nicht mit der Kopfleiste verschwimmt.
              filter:
                'drop-shadow(1px 0 0 #fff) drop-shadow(-1px 0 0 #fff) drop-shadow(0 1px 0 #fff) drop-shadow(0 -1px 0 #fff)',
            }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap component="div" sx={{ lineHeight: 1.2 }}>
              MyBudget
            </Typography>
            <Typography
              variant="caption"
              noWrap
              component="div"
              sx={{ display: { xs: 'none', sm: 'block' }, opacity: 0.85, lineHeight: 1.2 }}
            >
              Finanzen je Abteilung
            </Typography>
          </Box>
          <UserMenu />
        </Toolbar>
      </AppBar>

      {hasAccess && (
        <Box
          component="nav"
          sx={{
            width: { sm: drawerOpen ? DRAWER_WIDTH : 0 },
            flexShrink: { sm: 0 },
            displayPrint: 'none',
          }}
        >
          <Drawer
            variant="temporary"
            open={drawerOpen && isNarrow}
            onClose={() => setDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
          >
            <Sidebar onNavigate={() => setDrawerOpen(false)} />
          </Drawer>
          <Drawer
            variant="persistent"
            open={drawerOpen}
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
          >
            <Sidebar />
          </Drawer>
        </Box>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Without minWidth:0 a wide table forces the whole page to scroll horizontally.
          minWidth: 0,
          p: 3,
          width: { sm: hasAccess && drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%' },
          transition: (t) =>
            t.transitions.create('width', {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.leavingScreen,
            }),
          '@media print': { width: '100%', p: 0 },
        }}
      >
        <Toolbar sx={{ displayPrint: 'none' }} />
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>{hasAccess ? <Outlet /> : <NoAccess />}</Box>
      </Box>
    </Box>
  );
}
